import axios from 'axios';
import { config } from "./config";
import CryptoJS from 'crypto-js';
import * as globalFunction from './GlobalFunction';

const handleUnauthorized = (error) => {
  if (error?.response?.status === 401 && !error.config?.suppressSessionExpired) {
    globalFunction.handleError({
      status: 401,
      requestPath: getRequestPath(error.config),
      method: error.config?.method,
      sessionReason: error.response.headers?.['x-session-reason']
    });
  }
};

const getRequestPath = (requestConfig) => {
  try {
    return new URL(requestConfig?.url || '', requestConfig?.baseURL || config.baseUrl).pathname;
  } catch (_) {
    return String(requestConfig?.url || '').split('?')[0];
  }
};

export const httpClient = axios.create({
  baseURL: config.baseUrl, //YOUR_API_URL HERE
  headers: {
    'Content-Type': 'application/json'
  },
});

httpClient.interceptors.request.use(function (client) {
  // Do something before request is sent
  client.headers.Security = CryptoJS.AES.encrypt(globalFunction.getUTCDate(), CryptoJS.enc.Utf8.parse(config.aesKey), {
    iv: CryptoJS.enc.Utf8.parse(config.aesIV),
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  }).toString(); // Set your new security value here
  return client;
}, function (error) {
  // Do something with request error
  return Promise.reject(error);
});

httpClient.interceptors.response.use(
  response => {
    if (response.data?.code === 401 && !response.config?.suppressSessionExpired) {
      globalFunction.handleResultError({
        ...response.data,
        requestPath: getRequestPath(response.config),
        method: response.config?.method
      });
    }
    return response;
  },
  async (error) => {
    handleUnauthorized(error);
    const retryConfig = error.config;
    if (error.code === 'ERR_NETWORK' && retryConfig.method === 'get' && (!retryConfig._retryCount || retryConfig._retryCount < config.networkRetryCount)) {
      retryConfig._retryCount = (retryConfig._retryCount || 0) + 1;
      console.warn(`[httpClient] Network error, retry ${retryConfig._retryCount}/${config.networkRetryCount}: ${retryConfig.method.toUpperCase()} ${retryConfig.url}`);
      await new Promise(resolve => setTimeout(resolve, config.networkRetryDelay));
      return httpClient(retryConfig);
    }
    return Promise.reject(error);
  }
);

export const uploadClient = axios.create({
  baseURL: config.baseUrl, //YOUR_API_URL HERE
  // headers: {
  //   'Authorization': 'Bearer ' + localStorage.getItem('token')
  // },
});

uploadClient.interceptors.request.use(function (client) {
  // Do something before request is sent
  client.headers.Security = CryptoJS.AES.encrypt(globalFunction.getUTCDate(), CryptoJS.enc.Utf8.parse(config.aesKey), {
    iv: CryptoJS.enc.Utf8.parse(config.aesIV),
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  }).toString(); // Set your new security value here
  return client;
}, function (error) {
  // Do something with request error
  return Promise.reject(error);
});

uploadClient.interceptors.response.use(
  response => {
    if (response.data?.code === 401) {
      globalFunction.handleResultError({
        ...response.data,
        requestPath: getRequestPath(response.config),
        method: response.config?.method
      });
    }
    return response;
  },
  async (error) => {
    handleUnauthorized(error);
    const retryConfig = error.config;
    if (error.code === 'ERR_NETWORK' && retryConfig.method === 'get' && (!retryConfig._retryCount || retryConfig._retryCount < config.networkRetryCount)) {
      retryConfig._retryCount = (retryConfig._retryCount || 0) + 1;
      console.warn(`[uploadClient] Network error, retry ${retryConfig._retryCount}/${config.networkRetryCount}: ${retryConfig.method.toUpperCase()} ${retryConfig.url}`);
      await new Promise(resolve => setTimeout(resolve, config.networkRetryDelay));
      return uploadClient(retryConfig);
    }
    return Promise.reject(error);
  }
);

export const psmaClient = axios.create({
  baseURL: 'https://api.psma.com.au',
  headers: {
    'Content-Type': 'application/json',
    'Authorization': config.psmaKey
  },
});

export const googleMapClient = axios.create({
  baseURL: 'https://maps.googleapis.com',
});

export const routesClient = axios.create({
  baseURL: 'https://routes.googleapis.com',
});

// AI 代理用辅助：原生 fetch 不走 axios 拦截器，调后端 SSE/流式接口时要手动拼
// JWT + Security（AES 加密时间戳）两个头，这两行逻辑与上方 httpClient 拦截器等价
export function buildAuthHeaders(extra = {}) {
  const security = CryptoJS.AES.encrypt(
    globalFunction.getUTCDate(),
    CryptoJS.enc.Utf8.parse(config.aesKey),
    { iv: CryptoJS.enc.Utf8.parse(config.aesIV), mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
  ).toString();
  const token = localStorage.getItem('token');
  return {
    'Authorization': token ? 'Bearer ' + token : '',
    'Security': security,
    ...extra,
  };
}

// 拼后端绝对 URL（原生 fetch 不会自动加 baseURL，只能手动拼）
export function apiUrl(path) {
  const base = (config.baseUrl || '').replace(/\/$/, '');
  return base + '/' + String(path).replace(/^\//, '');
}

// 文档 AI 抽取专用调用：复用 httpClient（带 JWT+Security 头）。
// analyze 是长耗时 AI 调用 → 拉长 timeout；POST 不走 httpClient 的重试（其重试仅针对 GET），避免重复烧 token/重复写库。
export const aiExtractionApi = {
  analyze: (jobId, documentIds) =>
    httpClient.post('AI/DocumentExtraction/Analyze', { jobId, documentIds }, { timeout: 180000 }),
  apply: (payload) =>
    httpClient.post('AI/DocumentExtraction/Apply', payload, { timeout: 60000 }),
  getContactTypeOptions: (moduleId) =>
    httpClient.get('AI/DocumentExtraction/GetContactTypeOptions', { params: { moduleId } }),
};
